import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { SectionLoaderComponent } from '@shared/components/loader';
import { CardEmptyComponent } from '@shared/components/card';
import { ModalFormComponent } from '@shared/components/modal/modal-form/modal-form.component';
import { NoteViewComponent } from '@shared/components/note-view/note-view.component';
import { ModalConfig } from '@shared/components/modal/modal.interface';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { TargetDistributorService } from '../../../target-distributor.service';
import { ITargetDistributorMonitoring__DistributorDetail } from '../../../target-distributor-monitoring.interface';
import { SummaryDetailDistributorComponent } from '../summary-detail-distributor/summary-detail-distributor.component';
import { SectionDetailProdukComponent } from '../section-detail-produk/section-detail-produk.component';

/**
 * Payload untuk membuka modal. Berbentuk objek (bukan argumen posisional) supaya card
 * turunan bisa menambah field tanpa mengubah seluruh pemanggil.
 */
export interface IModalDetailDistributorPayload {
  targetDistributorId: string;
  distributorId: string;
  distributorName: string;
  /**
   * [MAI-1296] Program hanya punya satu tingkat (`target_term.level_enum` = LEVEL_1).
   * Mengontrol sub-baris label tingkat di kartu Target Program — backend mengirim
   * `current_level` "Tingkat 1" untuk kedua jenis program. Absen = dianggap bertingkat.
   */
  isSingleLevelProgram?: boolean;
}

/** `windowClass` modal ini; dipakai ulang saat menyembunyikan modal lewat `setHidden()`. */
const WINDOW_CLASS = 'modal-detail-distributor';

/** Kelas penyembunyi window + backdrop saat sub-modal menggantikan modal ini. */
const HIDDEN_CLASS = 'modal-detail-distributor--hidden';

/**
 * [MAI-2600] Technical Enabler — container modal "Detail per Distributor".
 *
 * Menyediakan header (nama distributor + tombol X), layout body, dan pemuatan data
 * ringkasan per distributor. Isi body diproyeksikan lewat `<ng-content>` sehingga card
 * turunan (MAI-2601 kartu summary, MAI-2606 Detail Produk, MAI-2608 banner PO) tinggal
 * menempel tanpa membuat modal sendiri.
 *
 * Standalone supaya entry point kedua (MAI-2430, tab "Program Target" di halaman Detail
 * Distributor) bisa mengimpor komponen ini langsung tanpa menarik `TargetDistributorModule`.
 */
@Component({
  selector: 'app-modal-detail-distributor',
  standalone: true,
  imports: [CommonModule, ModalFormComponent, SectionLoaderComponent, CardEmptyComponent, NoteViewComponent, SummaryDetailDistributorComponent, SectionDetailProdukComponent],
  templateUrl: './modal-detail-distributor.component.html',
  styleUrls: ['./modal-detail-distributor.component.scss'],
})
export class ModalDetailDistributorComponent {
  @ViewChild('modal') private modal!: ModalFormComponent;

  /** [MAI-2604] Diteruskan dari link "Lihat Detail" pada kartu Total Nilai Invoice. */
  @Output() openInvoiceDetail = new EventEmitter<void>();
  /** [MAI-2725] Diteruskan dari link "+N Hadiah Lainnya". */
  @Output() openOtherRewards = new EventEmitter<void>();
  /** [MAI-2608] Diteruskan dari tombol "Lihat PO" pada banner potensi PO. */
  @Output() openPotentialPo = new EventEmitter<void>();

  readonly STRING_CONSTANTS = STRING_CONSTANTS;

  payload: IModalDetailDistributorPayload | null = null;
  detail: ITargetDistributorMonitoring__DistributorDetail | null = null;
  isLoading = false;
  hasError = false;

  /**
   * `showFooter: false` — design tidak punya tombol aksi di bawah modal.
   * `modalConfig` wajib dikirim: `ModalFormComponent.ngOnInit` membacanya tanpa null guard.
   */
  modalConfig: ModalConfig = {
    modalTitle: '',
    showHeader: true,
    showFooter: false,
  };

  /**
   * Lebar modal 1000px (design node 8212-42251) tidak cocok dengan `size: 'lg'` (800px)
   * maupun `'xl'` (1140px), jadi diatur lewat `windowClass` di file scss.
   */
  readonly modalOptions: NgbModalOptions = {
    windowClass: WINDOW_CLASS,
    scrollable: true,
  };

  constructor(
    private readonly targetDistributorService: TargetDistributorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {}

  /**
   * `detectChanges()` sebelum `open()` wajib: NgbModal baru meng-instantiate `<ng-template>`
   * saat `open()` dipanggil, sehingga judul yang di-set pada baris sebelumnya harus sudah
   * ter-render lebih dulu.
   */
  open(payload: IModalDetailDistributorPayload): void {
    this.payload = payload;
    this.detail = null;
    this.hasError = false;
    this.isLoading = true;
    this.modalConfig = { ...this.modalConfig, modalTitle: payload.distributorName };
    this.cdr.detectChanges();
    this.modal.open();
    this.fetchDetail();
  }

  /** Menutup modal tidak memuat ulang tabel induk — tidak ada event yang di-emit. */
  close(): void {
    this.modal.close();
  }

  /**
   * [MAI-2604 QA] Sub-modal harus MENGGANTIKAN modal ini, bukan menumpuk di atasnya.
   *
   * Modal tidak ditutup, hanya window + backdrop-nya disembunyikan lewat kelas tambahan
   * (lihat file scss), supaya detail yang sudah dimuat dan posisi scroll tetap utuh —
   * menutup sub-modal cukup menampilkannya kembali tanpa request ulang, sesuai AC-01
   * "tombol X kembali ke modal Detail per Distributor".
   */
  setHidden(hidden: boolean): void {
    this.modal.update({
      windowClass: hidden ? `${WINDOW_CLASS} ${HIDDEN_CLASS}` : WINDOW_CLASS,
      backdropClass: hidden ? HIDDEN_CLASS : '',
    });
  }

  /**
   * [MAI-2608 AC-01] Banner tampil kalau distributor punya PO Siap Diproses / Diproses
   * Sebagian — termasuk pada program berstatus Selesai. AC awal meminta banner disembunyikan
   * untuk program Selesai; [QA MAI-1985] membatalkannya, jadi status program tidak lagi ikut
   * menentukan.
   *
   * Kontrak monitoring tidak mengirim jumlah PO maupun penanda "punya PO"; satu-satunya
   * sinyalnya adalah `potential_omzet`, yang non-nullable dan bernilai "Rp 0" saat tidak ada
   * PO. Jadi keberadaan PO diturunkan dari ada tidaknya angka bukan nol pada nominal itu —
   * dicek lewat regex, bukan dengan mem-parse string terformat menjadi number.
   */
  get hasPotentialPo(): boolean {
    return /[1-9]/.test(this.detail?.potential_omzet ?? '');
  }

  /** Nominal sudah terformat dari backend — disisipkan apa adanya ke dalam kalimat banner. */
  get potentialPoText(): string {
    return `Terdapat PO dengan potensi omzet ${this.detail?.potential_omzet} yang dapat menambah progres program.`;
  }

  private fetchDetail(): void {
    if (!this.payload) return;

    this.targetDistributorService
      .getMonitoringDistributorDetail(this.payload.targetDistributorId, this.payload.distributorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Service sudah menormalkan bentuk array/objek dari backend menjadi satu objek.
          this.detail = res?.data ?? null;
          this.hasError = !this.detail;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.detail = null;
          this.hasError = true;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }
}
